import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavSlot, usePageName } from '../pageName'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import type { ChatModel, ChatThread, Handover, ImageRef } from '@shared/services'
import { awaitRun } from '@shared/services/pictureService'
import {
  REF_DRAG_TYPE,
  createConversation,
  decodeRefs,
  encodeRef,
  listModels,
  readConversation,
  renameConversation,
  renameThread,
  sendMessage,
  measure,
  resolveImages,
  uploadProjectFile,
  uploadChatFile,
  isChatFile,
  withRetry,
} from '@shared/services'
import type { ChatFile } from '@shared/services'
import { useSkills } from '@shared/hooks/useSkills'
import { useProjectRemembered } from '../useRemembered'
import { useAuth } from '@shared/state/AuthContext'
import { initials } from '../components/initials'
import { forgetOpening, readOpening } from '../openProject'
import { ShareDialog } from '../components/ShareDialog'
import { ThreadPicker } from '../components/ThreadPicker'
import { PictureViewer } from '../components/PictureViewer'
import { animateLayout } from '../animateLayout'
import { ProjectCanvas, isPale, readBoardPaper } from '../canvas/ProjectCanvas'
import '../project.css'
// The light set, for a light paper (2026-10-21).
import '../board-pale.css'
import { WwLoaderFill } from '@shared/components/WwLoader'

/** A picture attached to a message: its name, and a link that loads now. */
interface Attached {
  ref: ImageRef
  url: string
  /**
   * The full-size file, when `url` is a smaller copy made for drawing. Opened
   * larger (PictureViewer), the picture fades from one to the other.
   */
  full?: string
}

interface Bubble {
  key: string
  role: 'user' | 'assistant'
  body: string
  model: string
  images?: Attached[]
  /**
   * Pictures this message is still waiting for.
   *
   * On the message rather than in the streaming view, and that is the whole
   * point of it existing. While the chat drew its pictures inside the answer's
   * own stream the empty frames could live there too -- the wait and the words
   * ended together. They no longer do: the turn ends in seconds and the
   * pictures arrive a minute later, against a message that was finished and
   * saved long before. Frames left in the streaming view were torn down with
   * it, so the whole wait happened with nothing on screen at all.
   */
  pending?: number
  /** The answer started and then stopped. Shown, but not passed off as whole. */
  partial?: boolean
  /** Documents sent with it (2026-10-14). */
  files?: ChatFile[]
  /**
   * Who wrote it, when it was not the person reading: a project shared with
   * them carries its earlier conversation, in its authors' names.
   */
  authorId?: string | null
  authorName?: string
  /** When it was said, for placing the hand-over lines between them. */
  at?: string
}

let counter = 0
const nextKey = () => `local-${(counter += 1)}`

/**
 * Add pictures to a message's, once each.
 *
 * A picture can reach a message by two roads at once: the conversation is read
 * with it already on the row, and the run it came from is also still being
 * watched. Showing it twice would look like two pictures were made.
 */
function mergeShots(have: Attached[] | undefined, adding: Attached[]): Attached[] {
  const out = [...(have ?? [])]
  for (const one of adding) {
    if (!out.some((seen) => seen.ref.store === one.ref.store && seen.ref.id === one.ref.id)) {
      out.push(one)
    }
  }
  return out
}

/** The same name the Worker gives it, so the header does not lag a round behind. */
/**
 * What is selected, as plain text (2026-10-23): line breaks where the page
 * breaks lines, a "•" or the number before each list item, nothing for
 * pictures and buttons, no formatting of any kind. Laid out off screen for
 * the moment it takes, because only laid-out text knows where its lines
 * break.
 */
function plainTextOf(chosen: Selection): string {
  const holder = document.createElement('div')
  holder.style.cssText = 'position:fixed;left:-100000px;top:0;width:800px;white-space:normal;'
  for (let i = 0; i < chosen.rangeCount; i += 1) holder.appendChild(chosen.getRangeAt(i).cloneContents())
  holder.querySelectorAll('img, svg, video, button, input, textarea, select').forEach((one) => one.remove())
  holder.querySelectorAll('ol').forEach((list) => {
    let n = Number(list.getAttribute('start')) || 1
    list.querySelectorAll(':scope > li').forEach((item) => {
      item.insertBefore(document.createTextNode(`${n}. `), item.firstChild)
      n += 1
    })
  })
  holder.querySelectorAll('ul > li').forEach((item) => item.insertBefore(document.createTextNode('• '), item.firstChild))
  document.body.appendChild(holder)
  const text = holder.innerText
  holder.remove()
  return text.replace(/\n{3,}/g, '\n\n').trim()
}

function titleFrom(message: string): string {
  const firstLine = message.split('\n')[0]!.trim()
  const short = firstLine.length > 60 ? `${firstLine.slice(0, 57).trimEnd()}…` : firstLine
  return short || 'Untitled project'
}

/**
 * A project: chat on the left, the canvas its output lands on in the middle,
 * the skill list on the right.
 *
 * The conversation is stored server-side — a project and its messages — so it
 * survives a reload and is readable from anywhere the person signs in. Nothing
 * about the model, the key or the cost is decided here; the browser sends a
 * message and receives text.
 */
/**
 * One thing the assistant said, with its face beside it.
 *
 * The picture is the same mark the site is called after and it never moves:
 * this runs beside text that is still being typed out, and a logo that
 * pulsed or spun there would compete with the only thing on the screen that
 * is genuinely happening.
 */
function Answer({ children }: { children: ReactNode }) {
  return (
    <div className="project__turn">
      {/* No face beside the assistant's words since 2026-10-21; yours stays. */}
      <div className="project__said">{children}</div>
    </div>
  )
}

/**
 * The line where a shared project changed hands: everything above it was said
 * before it was shared, everything below after.
 */
/**
 * When a message was said, for placing the hand-over lines.
 *
 * Compared as times, not as text: the database writes "+00:00" and six
 * decimals, a browser "Z" and three. A message sent during this visit has no
 * time yet, and is later than every hand-over.
 */
function timeOf(message: { at?: string }): number {
  return message.at ? Date.parse(message.at) : Infinity
}

function HandoverLine({ handover }: { handover: Handover }) {
  const on = new Date(handover.at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  return (
    <div className="project__handover" role="separator">
      <span>
        {handover.from} shared this with {handover.to} · {on}
      </span>
    </div>
  )
}

export function ProjectPage() {
  const { skills } = useSkills()

  /**
   * What the rail offers.
   *
   * No administrator tools: this is a working surface, and a tile that opens
   * something most people may not use reads as a fault rather than as a
   * permission. No panel-only skills either — they need a Miro board, which
   * this page is not. And only Design skills (Huanrui, 2026-10-02): the
   * canvas is where things are made; Research is looked at from the overview.
   */
  const canvasSkills = useMemo(
    () => (skills ?? []).filter((skill) => !skill.adminOnly && !skill.panelOnly && skill.area === 'design'),
    [skills],
  )
  const { projectId: routeId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  // The line typed on the overview: handed across tabs under a one-off key
  // (openProject.ts), or in the router's state from an older link.
  const openingKey = new URLSearchParams(location.search).get('opening')
  const [opening] = useState(
    () => readOpening(openingKey) || (location.state as { prompt?: string } | null)?.prompt || '',
  )

  const [conversationId, setConversationId] = useState(routeId ?? '')
  const [title, setTitle] = useState('Untitled project')
  // The project's title in the top bar, after WonderWorks (2026-10-19).
  usePageName(title)
  /** How this project reached its owner, oldest first. Empty if nobody shared it. */
  const [handovers, setHandovers] = useState<Handover[]>([])
  /*
   * Several conversations in one project (2026-10-22, docs/chat-threads.sql).
   * '' is the project's first conversation, 'new' one not sent yet (nothing
   * is written until its first message), otherwise a thread's id. The last
   * one open is remembered with the project.
   */
  const [keptThread, keepThread] = useProjectRemembered(routeId ?? '', 'thread', '')
  const [thread, setThread] = useState<string>(keptThread)
  const threadRef = useRef(thread)
  threadRef.current = thread
  const [threads, setThreads] = useState<ChatThread[]>([{ id: null, title: '', created_at: null }])
  /** A name given to a new conversation before its first message (2026-11-02): kept with it once sent. */
  const [newName, setNewName] = useState('')
  const newNameRef = useRef(newName)
  newNameRef.current = newName
  /** A conversation just made here, so its first read is not done over the answer arriving. */
  const skipLoad = useRef('')
  const [sharing, setSharing] = useState(false)
  const [messages, setMessages] = useState<Bubble[]>([])
  const [draft, setDraft] = useState('')
  /** Pictures waiting to go with the next message. Already uploaded. */
  const [attached, setAttached] = useState<Attached[]>([])
  /** Documents waiting to go with the next message. Already uploaded. */
  const [attachedFiles, setAttachedFiles] = useState<ChatFile[]>([])
  const [attaching, setAttaching] = useState(false)
  /**
   * What the model is doing before it speaks (2026-10-14): searching the web,
   * or thinking -- with the summary of its thinking as it streams.
   */
  const [searchingWeb, setSearchingWeb] = useState(false)
  const [thinking, setThinking] = useState('')
  /** The answer as it arrives, before it becomes a message of its own. */
  const [streaming, setStreaming] = useState('')
  /**
   * Pictures the model has made so far in the answer being written.
   *
   * Kept apart from the finished messages because they belong to a turn that
   * is not over. When it is over they move onto the message, and this empties
   * — which is what stops the same picture being on screen twice.
   */
  const [streamingShots, setStreamingShots] = useState<Attached[]>([])
  /** A picture in the conversation, opened larger (PictureViewer). */
  const [viewing, setViewing] = useState<Attached | null>(null)
  const closeViewer = useCallback(() => setViewing(null), [])
  /**
   * Pictures the model has started but not finished.
   *
   * A count rather than a list, because there is nothing to put in a list yet
   * — that is the whole problem it solves. Each one draws an empty frame that
   * a real picture replaces when it lands.
   */
  const [drawing, setDrawing] = useState(0)

  /**
   * Names into pictures, in the order they were made.
   *
   * Best effort on purpose: a link that cannot be fetched leaves that one
   * picture off the message rather than losing the message. It is stored
   * either way, so a reload will show it.
   */
  const attachedFor = useCallback(async (refs: ImageRef[]): Promise<Attached[]> => {
    if (refs.length === 0) return []
    const pictures = await resolveImages(refs).catch(() => [])
    return refs
      .map((ref) => {
        const found = pictures.find(
          (picture) => picture.store === ref.store && picture.id === ref.id,
        )
        return found ? { ref, url: found.originalPath } : null
      })
      .filter((one): one is Attached => one !== null)
  }, [])

  /**
   * Watch one picture run and show what it makes under one message.
   *
   * Used straight after a turn, and again when a conversation is reopened
   * while its pictures are still being drawn -- the same frames, filled the
   * same way, whichever of the two started the wait.
   *
   * Nothing is written from here. The queue consumer puts the pictures on the
   * saved message itself, which is what makes a closed tab harmless; this only
   * shows them, and takes this run's frames down whether or not it delivered,
   * because a frame that waits for ever is worse than a picture that did not
   * arrive.
   *
   * `signal` is for the reopened case: moving to another conversation stops
   * the watching, so a frame is never filled on a message that is no longer
   * on screen.
   */
  const watchRun = useCallback(
    async (key: string, run: { id: string; n: number }, signal?: AbortSignal) => {
      let refs: ImageRef[] = []
      try {
        const finished = await awaitRun(run.id, signal)
        if (finished.status === 'succeeded') {
          refs = finished.pictures.map((one) => ({ store: one.store, id: one.id }))
        }
      } catch (error) {
        console.warn('could not watch the picture run', run.id, error)
      }
      if (signal?.aborted) return

      const pictures = refs.length > 0 ? await attachedFor(refs) : []
      if (signal?.aborted) return
      setMessages((prev) =>
        prev.map((one) =>
          one.key === key
            ? {
                ...one,
                images: mergeShots(one.images, pictures),
                pending: Math.max(0, (one.pending ?? 0) - run.n),
              }
            : one,
        ),
      )
    },
    [attachedFor],
  )

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  /**
   * The conversation takes the whole window, with everything else put away.
   *
   * Remembered, like everything else about how this page is arranged. Somebody
   * who works full-window had been setting it again on every single visit,
   * which is a setting that exists and does not stick — the least useful kind.
   */
  // Per project since 2026-10-13: each opens the way it was last left;
  // a new one opens with the small conversation and the skills rail out.
  const [wide, setWide] = useProjectRemembered(conversationId, 'wide', false)
  /**
   * Folded away to a ball, the same as the skills rail.
   *
   * Nothing is unmounted when it folds — the messages, the scroll position and
   * a half-typed line are all still there. Collapsing a panel is a statement
   * about the screen, not about the conversation, and losing a draft to it
   * would be the panel answering a question nobody asked.
   */
  const [tucked, setTucked] = useProjectRemembered(conversationId, 'tucked', false)
  /**
   * The chat's width, dragged at the gutter beside it (Huanrui, 2026-10-21),
   * kept with the project. 0: the width it grows to on its own.
   */
  const [chatWidth, setChatWidth] = useProjectRemembered(conversationId, 'chatWidth', 0)
  const [sizing, setSizing] = useState<number | null>(null)
  const page = useRef<HTMLDivElement | null>(null)
  const startSizing = (event: React.PointerEvent<HTMLDivElement>) => {
    const box = chat.current?.getBoundingClientRect()
    const whole = page.current?.getBoundingClientRect()
    if (!box || !whole) return
    event.preventDefault()
    const handle = event.currentTarget
    handle.setPointerCapture(event.pointerId)
    const startX = event.clientX
    const startW = box.width
    const most = Math.max(320, Math.min(760, whole.width * 0.6))
    let now = startW
    const move = (moved: PointerEvent) => {
      now = Math.round(Math.min(most, Math.max(240, startW + moved.clientX - startX)))
      setSizing(now)
    }
    const up = () => {
      handle.removeEventListener('pointermove', move)
      handle.removeEventListener('pointerup', up)
      handle.removeEventListener('pointercancel', up)
      setSizing(null)
      if (Math.abs(now - startW) >= 1) setChatWidth(now)
    }
    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', up)
    handle.addEventListener('pointercancel', up)
    setSizing(startW)
  }
  const shownChatWidth = sizing ?? chatWidth
  /**
   * The ball opens and folds the conversation (2026-10-21): it grows out of
   * the ball and folds back into it. Filling the window and being folded away
   * are opposite answers to the same question, so one cancels the other.
   */
  const [chatClosing, setChatClosing] = useState(false)
  /**
   * Sliding in from the left edge (2026-10-22). Only when the W opens it: an
   * entrance played on every mount also played when coming back from full
   * window, under the view transition, which is what made that step jerk.
   */
  const [chatOpening, setChatOpening] = useState(false)
  /** The board's paper is light: the conversation and the tools take the light set (board-pale.css). */
  // Started from the paper this browser last had, so the page does not open
  // dark and then turn.
  const [boardPale, setBoardPale] = useState(() => isPale(readBoardPaper()))
  // The board's paper, so the full-window conversation stands on the same colour (2026-10-21).
  const [boardPaper, setBoardPaper] = useState<string | null>(() => readBoardPaper())
  const toggleChat = () => {
    if (chatClosing) return
    if (tucked) {
      setTucked(false)
      setChatOpening(true)
      window.setTimeout(() => setChatOpening(false), 260)
      return
    }
    if (wide) {
      animateLayout(() => {
        setWide(false)
        setTucked(true)
      })
      return
    }
    setChatClosing(true)
    window.setTimeout(() => {
      setTucked(true)
      setChatClosing(false)
    }, 200)
  }
  /*
   * Arriving with a line typed on the overview opens the conversation full
   * window (Huanrui, 2026-10-05) -- and that counts as the person's setting,
   * kept like any other: leave and come back to the project and it is still
   * full window, until it is changed by hand, which is then what is kept.
   */
  const openedWide = useRef(false)
  useEffect(() => {
    if (!opening || openedWide.current) return
    openedWide.current = true
    if (!wide) setWide(true)
    if (tucked) setTucked(false)
    // Once, on arrival; the remembered values are only read here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opening])
  const { user } = useAuth()
  const composer = useRef<HTMLTextAreaElement>(null)
  /** The panel itself, so the board can ask where it is when a drag ends. */
  const chat = useRef<HTMLElement>(null)
  /** True while pictures from the board are being held over the panel. */
  const [catching, setCatching] = useState(false)
  /** Your two letters — the same ones the badge in the top bar shows. */
  const you = user ? initials(user.displayName, user.email) : '?'
  /** The title being edited, or null when it is just being displayed. */
  const [renaming, setRenaming] = useState<string | null>(null)

  const [models, setModels] = useState<ChatModel[]>([])
  const [model, setModel] = useState('')

  const scroller = useRef<HTMLDivElement | null>(null)

  // Which models are on offer. Retried, because it runs once and a page that
  // lives for an hour must not be left without a picker by one refused request.
  useEffect(() => {
    let cancelled = false
    withRetry(() => listModels())
      .then((rows) => {
        if (cancelled) return
        setModels(rows)
        // The Worker says which one this person starts on; picking the first
        // row instead would silently ignore it.
        setModel((current) => current || (rows.find((row) => row.isDefault)?.slug ?? rows[0]?.slug ?? ''))
      })
      .catch(() => {
        /* the picker simply does not appear; the Worker still uses its default */
      })
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * A conversation this page created a moment ago.
   *
   * Putting its id in the address is what makes it survive a reload — but it
   * also makes the loader below think it has an existing conversation to fetch,
   * and that fetch would land mid-answer and wipe what is on screen. This says:
   * this one is already here.
   */
  const justCreated = useRef('')

  // An existing conversation, read back in full.
  useEffect(() => {
    if (!routeId) return
    const key = `${routeId}|${thread}`
    // Made in this tab a moment ago, or a conversation just started here:
    // what is on screen is already right, and a read now would land over the
    // answer arriving. Once only.
    if (justCreated.current === routeId) {
      justCreated.current = ''
      if (thread !== 'new') return
    }
    if (skipLoad.current === key) {
      skipLoad.current = ''
      return
    }
    // A new conversation, not sent yet: nothing to read (openThread has
    // already cleared the page).
    if (thread === 'new') return
    let cancelled = false
    const watching = new AbortController()
    withRetry(() => readConversation(routeId, thread || null))
      .then((conversation) => {
        if (cancelled) return
        setTitle(conversation.title)
        setHandovers(conversation.handovers)
        setThreads(conversation.threads)
        // One that has gone (or another person's) falls back to the first.
        if ((conversation.thread ?? '') !== thread) {
          skipLoad.current = `${routeId}|${conversation.thread ?? ''}`
          setThread(conversation.thread ?? '')
        }
        setMessages(
          conversation.messages.map((message) => {
            // Frames for the pictures still being drawn for this message.
            const waiting = conversation.pending
              .filter((run) => run.messageId === message.id)
              .reduce((sum, run) => sum + run.n, 0)
            return {
              key: message.id,
              role: message.role,
              body: message.body,
              model: message.model,
              files: message.files.length > 0 ? message.files : undefined,
              pending: waiting > 0 ? waiting : undefined,
              authorId: message.author_id ?? null,
              authorName: message.author_name ?? '',
              at: message.created_at,
            }
          }),
        )

        // And the wait for them, the same as straight after a turn.
        for (const run of conversation.pending) {
          void watchRun(run.messageId, { id: run.runId, n: run.n }, watching.signal)
        }

        // The pictures are stored as names; the links are fetched after the
        // words are on screen, so a conversation is readable immediately.
        const named = conversation.messages.flatMap((message) =>
          (message.images ?? []).map((ref) => ({ id: message.id, ref })),
        )
        if (named.length === 0) return
        void resolveImages(named.map((entry) => entry.ref))
          .then((pictures) => {
            if (cancelled) return
            const byRef = new Map(pictures.map((picture) => [`${picture.store}:${picture.id}`, picture]))
            setMessages((prev) =>
              prev.map((bubble) => {
                const mine = named.filter((entry) => entry.id === bubble.key)
                if (mine.length === 0) return bubble
                return {
                  ...bubble,
                  // Merged, not replaced: a run being watched may already have
                  // put a picture here, and replacing would take it away again.
                  images: mergeShots(
                    mine
                      .map((entry) => {
                        const picture = byRef.get(`${entry.ref.store}:${entry.ref.id}`)
                        return { ref: entry.ref, url: picture?.thumbnailPath ?? '', full: picture?.originalPath }
                      })
                      .filter((picture) => picture.url !== ''),
                    bubble.images ?? [],
                  ),
                }
              }),
            )
          })
          .catch(() => undefined)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not open this project')
      })
    return () => {
      cancelled = true
      watching.abort()
    }
  }, [routeId, thread, watchRun])

  /** Open another of the project's conversations, or start a new one. */
  const openThread = useCallback(
    (next: string) => {
      if (busy || next === threadRef.current) return
      setError('')
      setMessages([])
      setThread(next)
      setNewName('')
      if (next !== 'new') keepThread(next)
    },
    [busy, keepThread],
  )

  /**
   * Rename one of the project's conversations (double-click its name). Shown
   * at once, and put back if the Worker says no. `which` is '' for the first.
   */
  const renameOneThread = useCallback(
    async (which: string, title: string) => {
      // Not sent yet: nothing to rename on the server; the name waits for the first message.
      if (which === 'new') {
        setNewName(title)
        return
      }
      if (!routeId) return
      const id = which || null
      const before = threads.find((one) => one.id === id)?.title ?? ''
      const show = (name: string) => setThreads((was) => was.map((one) => (one.id === id ? { ...one, title: name } : one)))
      show(title)
      try {
        show(await renameThread(routeId, id, title))
      } catch (failure) {
        show(before)
        setError(failure instanceof Error ? failure.message : 'Could not rename that conversation')
      }
    },
    [routeId, threads],
  )

  /**
   * The project, made on demand and only once.
   *
   * The promise is held rather than a boolean, because two things can ask at
   * the same moment — a message being sent while the canvas is settling — and
   * two calls would make two projects, leaving the person's work split across
   * a project they can see and one they cannot.
   */
  const making = useRef<Promise<string> | null>(null)

  const ensureProject = useCallback(async () => {
    if (conversationId) return conversationId
    if (!making.current) {
      making.current = createConversation().then((made) => {
        justCreated.current = made.id
        setConversationId(made.id)
        navigate(`/projects/${made.id}`, { replace: true })
        return made.id
      })
    }
    return making.current
  }, [conversationId, navigate])

  /**
   * Take pictures for the next message.
   *
   * Uploaded as they are chosen rather than when Send is pressed: the wait
   * belongs to the moment of dropping, not to the moment of asking, and a
   * message that takes ten seconds to leave reads as a broken model.
   */
  /**
   * Pictures that are already stored — dragged off the board, or made by a
   * skill. Nothing is uploaded: they have names, and the names are all a
   * message needs. Anything already waiting is left alone rather than added
   * twice, because dropping the same picture again is far more likely to be a
   * slip than a request for two of it.
   */
  const attachRefs = useCallback(async (refs: ImageRef[]) => {
    if (refs.length === 0) return
    setAttaching(true)
    try {
      const pictures = await resolveImages(refs)
      setAttached((prev) => {
        const already = new Set(prev.map((one) => `${one.ref.store}:${one.ref.id}`))
        const fresh = pictures
          .filter((picture) => !already.has(`${picture.store}:${picture.id}`))
          .map((picture) => ({
            ref: { store: picture.store, id: picture.id } as ImageRef,
            url: picture.originalPath,
          }))
        return [...prev, ...fresh]
      })
    } catch {
      setError('Those pictures could not be added')
    } finally {
      setAttaching(false)
    }
  }, [])

  const attach = useCallback(
    async (files: File[]) => {
      const images = files.filter((file) => file.type.startsWith('image/'))
      // Documents (2026-10-14): PDF, Office and text files go to the model whole.
      const documents = files.filter((file) => !file.type.startsWith('image/') && isChatFile(file))
      if (images.length === 0 && documents.length === 0) {
        if (files.length > 0) setError('Pictures, PDF, Word, Excel, PowerPoint or text files only')
        return
      }
      setAttaching(true)
      try {
        const id = await ensureProject()
        for (const file of documents) {
          const kept = await uploadChatFile(id, file)
          setAttachedFiles((prev) => (prev.some((one) => one.key === kept.key) ? prev : [...prev, kept]))
        }
        for (const file of images) {
          const size = await measure(file)
          const stored = await uploadProjectFile(id, file, size)
          setAttached((prev) => [
            ...prev,
            { ref: { store: 'project', id: stored.id }, url: stored.url },
          ])
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'That could not be attached')
      } finally {
        setAttaching(false)
      }
    },
    [ensureProject],
  )

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if ((!trimmed && attached.length === 0 && attachedFiles.length === 0) || busy) return

      setError('')
      setBusy(true)
      setDraft('')
      const going = attached
      const goingFiles = attachedFiles
      setAttached([])
      setAttachedFiles([])
      setSearchingWeb(false)
      setThinking('')
      setMessages((prev) => [
        ...prev,
        {
          key: nextKey(),
          role: 'user',
          body: trimmed,
          model: '',
          images: going,
          files: goingFiles.length > 0 ? goingFiles : undefined,
        },
      ])

      // Accumulated here as well as in state: the catch below needs whatever
      // arrived before it broke, and reading it out of state would give the
      // value from the render this function was created in.
      let answer = ''
      // Held here rather than read back out of state for the same reason the
      // answer is: this function was created in one render and the pictures
      // arrive across several, so state would hand back the value from the
      // render this closure remembers.
      /**
       * The pictures the model made, as names, recorded the moment they are
       * announced.
       *
       * Names and not links, because turning a name into a link is a fetch and
       * a fetch can come back after the turn is over. The finished message is
       * built from these afterwards, where there is time to wait for them — it
       * used to be built from whatever the fetches happened to have delivered
       * by then, which on a slow one was nothing at all, and the picture only
       * appeared on reload.
       */
      const refsMade: ImageRef[] = []
      /**
       * Is this turn still the one on screen?
       *
       * A picture arrives as a name and is then fetched, and that fetch can
       * come back after the turn is over — at which point the picture is
       * already on the finished message, and putting it into the streaming
       * block as well puts the same picture on screen twice. Two avatars, and
       * the lower one not draggable, which is what gave it away: the finished
       * message's pictures can be dragged onto the board and a streaming
       * preview cannot.
       *
       * The first attempt at this guarded on a turn counter, which only moves
       * when the NEXT message is sent — so an answer landing in the gap
       * between this turn ending and the next one starting matched, and was
       * let through. "A newer turn has started" and "this turn is over" are
       * not the same question, and only the second one was ever being asked.
       */
      let live = true
      setStreaming('')
      setStreamingShots([])
      setDrawing(0)

      try {
        // One way in. The Worker will also create a project when it is not
        // given one, so a message sent while the canvas was settling would
        // have made a second — the person's work split between a project they
        // can see and one they cannot.
        const id = await ensureProject()

        const result = await sendMessage({
          message: trimmed,
          images: going.map((picture) => picture.ref),
          files: goingFiles,
          projectId: id || undefined,
          threadId: threadRef.current || undefined,
          model: model || undefined,
          onDelta: (piece) => {
            answer += piece
            setSearchingWeb(false)
            setStreaming(answer)
          },
          onSearching: () => setSearchingWeb(true),
          // The line was lost and the saved answer found (chatService.ts):
          // that, whole, in place of what had arrived -- with the pictures
          // already saved on it, added to the ones announced before the line
          // went, once each.
          onRecovered: (whole, saved) => {
            answer = whole
            for (const ref of saved) {
              if (!refsMade.some((one) => one.store === ref.store && one.id === ref.id)) refsMade.push(ref)
            }
            setSearchingWeb(false)
            setStreaming(whole)
          },
          onThinking: (piece) => {
            setSearchingWeb(false)
            setThinking((was) => was + piece)
          },
          onPictureStart: () => setDrawing((count) => count + 1),
          onPictureFailed: (reason) => {
            setDrawing((count) => Math.max(0, count - 1))
            setError(reason)
          },
          onStart: (started, startedThread) => {
            // A new conversation now has an id: it stays on screen as it is
            // (no read over the answer arriving) and joins the list.
            if (startedThread && threadRef.current === 'new') {
              skipLoad.current = `${started}|${startedThread}`
              setThread(startedThread)
              keepThread(startedThread)
              // Named before it was sent: that name, kept now that it exists.
              const named = newNameRef.current.trim()
              setNewName('')
              setThreads((was) => [
                ...was,
                { id: startedThread, title: named || titleFrom(trimmed), created_at: new Date().toISOString() },
              ])
              if (named && started) {
                void renameThread(started, startedThread, named).catch((failure: unknown) =>
                  setError(failure instanceof Error ? failure.message : 'Could not name that conversation'),
                )
              }
            }
            // The project already exists by now; this only names it, the first
            // time, after the line that created it.
            if (!started || title !== 'Untitled project') return
            setTitle(titleFrom(trimmed))
          },
        })

        // The answer goes up now, with whatever pictures already exist --
        // usually none, because they are being queued rather than drawn.
        const shots = await attachedFor(refsMade)
        const mine = nextKey()
        const coming = result.runs.reduce((sum, one) => sum + one.n, 0)
        setMessages((prev) => [
          ...prev,
          {
            key: mine,
            role: 'assistant',
            body: answer,
            model: result.model,
            images: shots,
            pending: coming,
          },
        ])

        /*
         * The pictures, afterwards, on their own time.
         *
         * Not awaited before the message is shown: waiting would put the turn
         * back inside the same request that had to end, which is what this
         * whole change exists to stop. Each run is watched, and as it lands
         * the picture is shown on the message. Saving it onto the row is the
         * server's job, not this page's, so closing the tab loses nothing.
         *
         * Deliberately not tied to `live`: this message is saved and on
         * screen, and a picture arriving for it is welcome whether or not
         * another turn has started since.
         */
        if (result.runs.length > 0 && result.messageId) {
          /*
           * Each run on its own, rather than all of them together.
           *
           * Two runs finishing a minute apart should put their pictures up a
           * minute apart. Waiting for the slowest before showing any would
           * make the quick one look as slow as it, and would leave every frame
           * empty until the last one landed.
           */
          for (const run of result.runs) {
            void watchRun(mine, run)
          }
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'The model could not be reached')
        // Half an answer is still worth showing — but labelled, not passed off
        // as the whole thing.
        if (answer) {
          // Resolved before the updater, not inside it: `setMessages` takes a
          // plain function and awaiting in there is awaiting in the wrong
          // place entirely.
          //
          // A picture that was made and paid for before the answer broke is
          // still a picture. Dropping it here would be throwing away the
          // expensive half of a failed turn.
          const shots = await attachedFor(refsMade)
          setMessages((prev) => [
            ...prev,
            { key: nextKey(), role: 'assistant', body: answer, model, partial: true, images: shots },
          ])
        }
      } finally {
        // Before the clear, not after: a fetch that lands between these two
        // lines would otherwise refill the list that is about to be emptied.
        live = false
        setStreaming('')
        setStreamingShots([])
        setDrawing(0)
        setSearchingWeb(false)
        setThinking('')
        setBusy(false)
      }
    },
    [attached, attachedFiles, busy, conversationId, ensureProject, keepThread, model, title, watchRun],
  )

  /**
   * Save the new name.
   *
   * Shown immediately and saved behind, like the pins — and put back if the
   * save fails, because a name that silently reverts on the next visit is
   * worse than one that never changed.
   */
  const commitRename = useCallback(
    (next: string) => {
      const wanted = next.trim()
      setRenaming(null)
      if (!wanted || wanted === title) return
      if (!conversationId) {
        // Nothing to save it to yet. It becomes the title the moment the first
        // message creates the conversation, so this is not worth an error.
        setTitle(wanted)
        return
      }
      const before = title
      setTitle(wanted)
      void renameConversation(conversationId, wanted).catch(() => {
        setTitle(before)
        setError('That name could not be saved')
      })
    },
    [conversationId, title],
  )

  // The line typed on the overview page, sent once and only once. A new project
  // opens straight into an answer rather than into an empty box.
  const openingSent = useRef(false)
  useEffect(() => {
    if (openingSent.current || !opening || routeId) return
    openingSent.current = true
    forgetOpening(openingKey)
    void send(opening)
  }, [opening, openingKey, routeId, send])

  /**
   * Keep the box the height of what is in it.
   *
   * Reset to `auto` first: `scrollHeight` is how tall the content needs to be
   * inside the box it already has, so measuring without letting it collapse
   * first means a box that grows and never shrinks again.
   */
  useEffect(() => {
    const box = composer.current
    if (!box) return
    box.style.height = 'auto'
    box.style.height = `${Math.min(box.scrollHeight, 200)}px`
  }, [draft])

  /**
   * Open on the latest message, and stay there.
   *
   * Scrolling to the bottom once, when the words arrive, was not enough. The
   * pictures load after the words, each one pushing the end further down, so
   * a conversation opened somewhere above its last message. And a folded-away
   * panel is `display: none`, which throws its scroll position away: unfolded,
   * it showed the first message instead of the last.
   *
   * So: whenever the list changes size — unfolded, widened, a picture landed —
   * it goes back to the bottom, but only while it was at the bottom. Somebody
   * who has scrolled up to read something is left where they are.
   */
  const following = useRef(true)
  const toLatest = useCallback(() => {
    const box = scroller.current
    if (box) box.scrollTop = box.scrollHeight
  }, [])

  // Follow the answer as it grows.
  useEffect(() => {
    following.current = true
    toLatest()
  }, [messages, streaming, toLatest])

  useEffect(() => {
    const box = scroller.current
    if (!box) return
    const onScroll = () => {
      // A hidden panel measures as nothing at all; that is not the reader
      // scrolling away.
      if (box.clientHeight === 0) return
      following.current = box.scrollHeight - box.scrollTop - box.clientHeight < 48
    }
    const settle = () => {
      if (following.current) toLatest()
    }
    // `load` does not bubble, so it is caught on the way down instead.
    box.addEventListener('load', settle, true)
    box.addEventListener('scroll', onScroll, { passive: true })
    const resized = new ResizeObserver(settle)
    resized.observe(box)
    return () => {
      box.removeEventListener('load', settle, true)
      box.removeEventListener('scroll', onScroll)
      resized.disconnect()
    }
  }, [toLatest])

  /**
   * Full window and back, keeping the reader's place (2026-10-22).
   *
   * Back from full window the text is narrower and so taller, and the
   * follower above only put the end back in view once the page was already
   * moving -- the jump Huanrui saw. Here the place is set inside the change,
   * before the browser takes its picture of the new layout: the end of the
   * conversation for someone at the end, otherwise the same message at the
   * same height.
   */
  const toggleWide = () => {
    const box = scroller.current
    let anchor: { line: Element; offset: number } | null = null
    if (box && !following.current) {
      const top = box.getBoundingClientRect().top
      const line = [...box.children].find((child) => child.getBoundingClientRect().bottom > top)
      if (line) anchor = { line, offset: line.getBoundingClientRect().top - top }
    }
    animateLayout(
      () => setWide(!wide),
      () => {
        const now = scroller.current
        if (!now) return
        if (!anchor) {
          now.scrollTop = now.scrollHeight
          return
        }
        now.scrollTop += anchor.line.getBoundingClientRect().top - now.getBoundingClientRect().top - anchor.offset
      },
    )
  }

  const empty = messages.length === 0 && !streaming
  /**
   * The project's name, in the top bar beside WONDERWORKS (2026-10-21): click
   * to rename. A new project is "Untitled Project".
   */
  const navSlot = useNavSlot()
  const shownTitle = title.trim().toLowerCase() === 'untitled project' ? 'Untitled Project' : title
  const titleBar = (
    <header className="project__namebar">
      <div className="project__head-row">
        {renaming === null ? (
          <button
            type="button"
            className="project__title"
            onClick={() => setRenaming(title)}
            title={`${title} · click to rename`}
          >
            {shownTitle}
          </button>
        ) : (
          <input
            className="project__title-input"
            // Grows with the name as it is typed; CSS field-sizing does it
            // exactly where the browser has it, this where it does not.
            size={Math.max(4, renaming.length + 1)}
            value={renaming}
            autoFocus
            maxLength={120}
            onChange={(event) => setRenaming(event.target.value)}
            onBlur={() => commitRename(renaming)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commitRename(renaming)
              // Escape leaves the old name alone. An edit box with no way
              // out but saving is a trap.
              if (event.key === 'Escape') setRenaming(null)
            }}
            aria-label="Conversation name"
          />
        )}
      </div>
      {/* No model picker since 2026-10-21: the default model is used. */}
    </header>
  )

  return (
    <div
      ref={page}
      className={`project${wide ? ' project--wide' : ''}${tucked ? ' project--tucked' : ''}${sizing !== null ? ' project--sizing' : ''}${boardPale ? ' project--pale' : ''}`}
      style={
        {
          ...(!wide && !tucked && shownChatWidth > 0 ? { '--chat-w': `${shownChatWidth}px` } : {}),
          ...(boardPaper ? { '--project-paper': boardPaper } : {}),
        } as React.CSSProperties
      }
    >
      {/* ---------- Chat ---------- */}
      <aside
        ref={chat}
        className={`project__chat${tucked ? ' project__chat--shut' : ' project__chat--open'}${chatClosing ? ' project__chat--closing' : ''}${
          chatOpening ? ' project__chat--opening' : ''
        }${
          catching ? ' project__chat--catching' : ''
        }`}
        onDragOver={(event) => {
          if (
            event.dataTransfer.types.includes('Files') ||
            event.dataTransfer.types.includes(REF_DRAG_TYPE)
          ) {
            event.preventDefault()
            event.dataTransfer.dropEffect = 'copy'
          }
        }}
        onDrop={(event) => {
          const carried = event.dataTransfer.getData(REF_DRAG_TYPE)
          if (carried) {
            event.preventDefault()
            try {
              const names: unknown = JSON.parse(carried)
              if (Array.isArray(names)) void attachRefs(decodeRefs(names as string[]))
            } catch {
              /* not something this panel knows how to take */
            }
            return
          }
          const dropped = [...event.dataTransfer.files]
          if (dropped.length === 0) return
          event.preventDefault()
          void attach(dropped)
        }}
      >
        {/* The ball: shut, it is all there is; open, it stays where it was,
            at the panel's top left, and pressing it again folds the panel
            back into it (Huanrui, 2026-10-21). A white W. */}
        <button
          type="button"
          className="project__ball"
          onClick={toggleChat}
          aria-expanded={!tucked && !chatClosing}
          aria-label={tucked ? 'Show the conversation' : 'Fold the conversation away'}
          title={tucked ? 'Conversation' : 'Fold away'}
        >
          {/* Shut: a white W. Open: the W in its colours, and the ring of the
              logo turning in round it once (2026-10-21). */}
          <img className="project__ball-ring" src="/brand/logo-ring.svg" alt="" />
          <img className="project__ball-w project__ball-w--white" src="/brand/logo-mark.svg" alt="" />
          <img className="project__ball-w project__ball-w--colour" src="/brand/logo-mark.svg" alt="" />
        </button>

        {navSlot ? createPortal(titleBar, navSlot) : null}

        {/*
          The sheet: everything you read and write, as one surface.

          It exists for the full-window mode. Letting the panel itself fill
          the window turned the whole screen one flat grey, and the
          assistant's bubbles — a grey a shade off that one — vanished into
          it. So the panel goes transparent there and this sits in the middle
          of the black instead, at a width prose can actually be read at.
          In the narrow column it is nothing at all: no border, no
          background, just the box that holds these three.
        */}
        <div className="project__sheet">
          {/* The top of the conversation (2026-10-22): which conversation, on
              the left, and full window on the right, floating over the text. */}
          <div className="project__chat-top">
            <ThreadPicker
              threads={threads}
              current={thread}
              onOpen={openThread}
              onRename={routeId ? renameOneThread : undefined}
              newName={newName}
              disabled={busy}
            />
          <button
            type="button"
            className="project__expand project__fill"
            onClick={toggleWide}
            aria-pressed={wide}
            title={wide ? 'Show the canvas again' : 'Fill the window'}
            aria-label={wide ? 'Show the canvas again' : 'Fill the window'}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              {wide ? (
                <path
                  d="M6 1.5v4.5H1.5M8 12.5V8h4.5"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : (
                <path
                  d="M1.5 5.5V1.5H5.5M12.5 8.5v4H8.5"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
            </svg>
          </button>
          </div>
          <div
            className="project__messages"
            ref={scroller}
            // Copied out of the conversation: the words only (Huanrui,
            // 2026-10-23). The browser's own copy took the bubbles' grey,
            // the colours and the typeface along into Word or Miro.
            onCopy={(event) => {
              const target = event.target as HTMLElement
              if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return
              const chosen = window.getSelection()
              if (!chosen || chosen.isCollapsed) return
              const text = plainTextOf(chosen)
              if (!text) return
              event.clipboardData.setData('text/plain', text)
              event.preventDefault()
            }}
          >
            {/* Nothing written in an empty conversation (2026-10-21). */}

            {messages.map((message, index) => {
              /*
                The pictures stand on their own, outside the bubble.
                
                Inside it they were a photograph with a thick white frame
                painted round it — the bubble's own background and padding,
                doing to a picture what they are there to do for a sentence.
                A picture needs no help being read; it needs to not be fenced.
                
                Which also means a message can be pictures and nothing else,
                with no empty bubble underneath.
              */
              const waiting = message.pending ?? 0
              const shots =
                (message.images && message.images.length > 0) || waiting > 0 ? (
                  <span className="project__shots">
                    {(message.images ?? []).map((picture) => (
                      <img
                        key={`${picture.ref.store}:${picture.ref.id}`}
                        className="project__shot project__shot--opens"
                        src={picture.url}
                        alt=""
                        title="Open larger"
                        onClick={() => setViewing(picture)}
                        /* Carried by name rather than as a file: it is already
                           stored, so the board only has to be told which one
                           it is — nothing is uploaded a second time. */
                        draggable
                        onDragStart={(event) => {
                          event.dataTransfer.setData(
                            REF_DRAG_TYPE,
                            JSON.stringify([encodeRef(picture.ref)]),
                          )
                          event.dataTransfer.effectAllowed = 'copy'
                        }}
                      />
                    ))}
                    {/* One empty frame per picture still being made. The same
                        shape the finished one takes, so nothing jumps when it
                        arrives -- and it lives on the message, so the wait is
                        visible long after the turn itself has ended. */}
                    {Array.from({ length: waiting }, (_, index) => (
                      <span
                        key={`waiting-${index}`}
                        className="project__shot project__shot--making"
                      >
                        <WwLoaderFill label="Making a picture" />
                      </span>
                    ))}
                  </span>
                ) : null

              const said =
                message.body || message.partial ? (
                  <div
                    className={`project__bubble project__bubble--${
                      message.role === 'user' ? 'you' : 'wonderworks'
                    }`}
                  >
                    {message.role === 'assistant' ? <RichText text={message.body} /> : message.body}
                    {message.partial ? (
                      <span className="project__partial">stopped early</span>
                    ) : null}
                  </div>
                ) : null

              // Somebody else's words, from before the project was shared:
              // their initials, and their name on hover.
              const theirs =
                message.role === 'user' &&
                Boolean(message.authorId) &&
                message.authorId !== user?.id &&
                Boolean(message.authorName)

              // The hand-overs that happened between the message before this
              // one and this one.
              const now = timeOf(message)
              const before = index > 0 ? timeOf(messages[index - 1]) : -Infinity
              const lines = handovers.filter((one) => {
                const at = Date.parse(one.at)
                return at <= now && at > before
              })

              const turn =
                message.role === 'user' ? (
                  <div key={message.key} className="project__turn project__turn--you">
                    <div className="project__said">
                      {theirs ? <span className="project__author">{message.authorName}</span> : null}
                      {shots}
                      {message.files && message.files.length > 0 ? (
                        <span className="project__docs">
                          {message.files.map((file) => (
                            <span key={file.key} className="project__doc" title={file.name}>
                              <span className="project__doc-kind">{kindOf(file.name)}</span>
                              <span className="project__doc-name">{file.name}</span>
                            </span>
                          ))}
                        </span>
                      ) : null}
                      {said}
                    </div>
                    <span
                      className={`project__avatar project__avatar--initials${
                        theirs ? ' project__avatar--other' : ''
                      }`}
                      aria-hidden="true"
                      title={theirs ? message.authorName : undefined}
                    >
                      {theirs ? initials(message.authorName ?? '', '') : you}
                    </span>
                  </div>
                ) : (
                  <Answer key={message.key}>
                    {shots}
                    {said}
                  </Answer>
                )

              return lines.length > 0 ? (
                <div key={message.key} className="project__handover-group">
                  {lines.map((one) => (
                    <HandoverLine key={one.at} handover={one} />
                  ))}
                  {turn}
                </div>
              ) : (
                turn
              )
            })}

            {/* A hand-over after the last message: shared, and nobody has said
                anything since. */}
            {handovers
              .filter((one) => {
                const last = messages[messages.length - 1]
                return !last || Date.parse(one.at) > timeOf(last)
              })
              .map((one) => (
                <HandoverLine key={`end-${one.at}`} handover={one} />
              ))}

            {streaming || streamingShots.length > 0 || drawing > 0 ? (
              <Answer>
                {/* A picture appears the moment it is made, above whatever the
                    answer has said so far. Waiting for the words to finish
                    would mean the slowest part of the turn producing nothing
                    visible for a minute. */}
                {streamingShots.length > 0 || drawing > 0 ? (
                  <span className="project__shots">
                    {streamingShots.map((picture) => (
                      <img
                        key={`${picture.ref.store}:${picture.ref.id}`}
                        className="project__shot project__shot--opens"
                        src={picture.url}
                        alt=""
                        title="Open larger"
                        onClick={() => setViewing(picture)}
                      />
                    ))}
                    {/* An empty frame per picture still being drawn. The same
                        shape the finished one will have, so nothing jumps when
                        it arrives. */}
                    {Array.from({ length: drawing }, (_, index) => (
                      <span key={`drawing-${index}`} className="project__shot project__shot--making">
                        <WwLoaderFill label="Making a picture" />
                      </span>
                    ))}
                  </span>
                ) : null}
                {streaming ? (
                  <div className="project__bubble project__bubble--wonderworks">
                    <RichText text={streaming} />
                    <span className="project__caret" aria-hidden="true" />
                  </div>
                ) : null}
              </Answer>
            ) : null}

            {busy && !streaming ? (
              searchingWeb ? (
                <p className="project__placeholder">Searching the web…</p>
              ) : thinking ? (
                // The latest of its thinking, as it streams: the last few
                // lines, not the whole essay.
                <p className="project__placeholder project__thinking">{lastLines(thinking)}</p>
              ) : (
                <p className="project__placeholder">Thinking…</p>
              )
            ) : null}

            {error ? <div className="ww-error">{error}</div> : null}
          </div>

          {attached.length > 0 || attachedFiles.length > 0 || attaching ? (
            <div className="project__attached">
              {attachedFiles.map((file) => (
                <span key={file.key} className="project__doc project__doc--waiting" title={file.name}>
                  <span className="project__doc-kind">{kindOf(file.name)}</span>
                  <span className="project__doc-name">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => setAttachedFiles((prev) => prev.filter((one) => one.key !== file.key))}
                    aria-label={`Remove ${file.name}`}
                    title="Remove"
                  >
                    ×
                  </button>
                </span>
              ))}
              {attached.map((picture, index) => (
                <span key={`${picture.ref.id}-${index}`} className="project__attached-item">
                  <img
                    src={picture.url}
                    alt=""
                    draggable
                    onDragStart={(event) => {
                      event.dataTransfer.setData(
                        REF_DRAG_TYPE,
                        JSON.stringify([encodeRef(picture.ref)]),
                      )
                      event.dataTransfer.effectAllowed = 'copy'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setAttached((prev) => prev.filter((_, i) => i !== index))}
                    aria-label="Remove this picture"
                    title="Remove"
                  >
                    ×
                  </button>
                </span>
              ))}
              {attaching ? <span className="project__attached-wait">adding…</span> : null}
            </div>
          ) : null}

          <form
            className="project__composer"
            onSubmit={(event) => {
              event.preventDefault()
              void send(draft)
            }}
          >
            {/*
              One box (2026-10-22): what you write at the top, growing with
              it; + (attach) at its foot on the left and send on the right.
            */}
            <textarea
              ref={composer}
              className="project__composer-input"
              rows={1}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                // Enter sends, Shift+Enter starts a line. The form's own submit
                // no longer covers this: a textarea treats Enter as text.
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  if (!busy && (draft.trim() || attached.length > 0 || attachedFiles.length > 0)) void send(draft)
                }
              }}
              // On the field rather than the window: the canvas has a paste
              // handler of its own, and this one only exists while the composer
              // has the cursor.
              onPaste={(event) => {
                const images = [...event.clipboardData.items]
                  .filter((item) => item.kind === 'file')
                  .map((item) => item.getAsFile())
                  .filter((file): file is File => Boolean(file) && (file!.type.startsWith('image/') || isChatFile(file!)))
                if (images.length === 0) return
                event.preventDefault()
                void attach(images)
              }}
              // A placeholder again (2026-10-22): the box is a full row now,
              // with room for it.
              placeholder="Ask WonderWorks anything"
              aria-label="Message WonderWorks"
              disabled={busy}
            />
            <div className="project__composer-row">
              {/* + (2026-10-14, a paper clip until 2026-10-22): pictures and documents
                  from the computer. A label, so the hidden input it wraps is
                  opened by the browser. */}
              <label
                className={`project__clip${busy || attaching ? ' project__clip--off' : ''}`}
                title="Attach pictures or documents (PDF, Word, Excel, PowerPoint, text)"
                aria-label="Attach pictures or documents"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
                  <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                <input
                  type="file"
                  multiple
                  hidden
                  disabled={busy || attaching}
                  accept="image/*,.pdf,.docx,.xlsx,.pptx,.txt,.md,.csv,.json"
                  onChange={(event) => {
                    const chosen = [...(event.target.files ?? [])]
                    // Cleared, so choosing the same file again still counts.
                    event.target.value = ''
                    if (chosen.length > 0) void attach(chosen)
                  }}
                />
              </label>
              <button
                type="submit"
                className="project__send"
                disabled={(!draft.trim() && attached.length === 0 && attachedFiles.length === 0) || busy}
                aria-label="Send"
                title="Send (Enter)"
              >
                <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
                  <path d="M8 13V3.5M3.5 8 8 3.5 12.5 8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </form>
        </div>
      </aside>

      {/* The gutter between the chat and the canvas: drag to size the chat. */}
      {!wide && !tucked ? (
        <div
          className={`project__gutter${sizing !== null ? ' project__gutter--on' : ''}`}
          role="separator"
          aria-orientation="vertical"
          aria-label="Drag to change the conversation's width"
          title="Drag to resize · double-click for the usual width"
          onPointerDown={startSizing}
          onDoubleClick={() => setChatWidth(0)}
        />
      ) : null}

      {/* ---------- Canvas and the rail beside it ----------
          One component, because dragging a skill from the rail onto the canvas
          has to turn a screen position into a canvas position, and only the
          canvas knows where it has been panned and zoomed to. */}
      <ProjectCanvas
        projectId={conversationId}
        skills={canvasSkills}
        ensureProject={ensureProject}
        chatArea={() => (tucked ? null : (chat.current?.getBoundingClientRect() ?? null))}
        onPicturesToChat={(refs) => void attachRefs(refs)}
        onOverChat={setCatching}
        onPale={setBoardPale}
        onPaper={setBoardPaper}
      />

      {sharing && conversationId ? (
        <ShareDialog projectId={conversationId} title={title} onClose={() => setSharing(false)} />
      ) : null}
      {viewing ? <PictureViewer src={viewing.url} full={viewing.full} onClose={closeViewer} /> : null}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * The answer's words (2026-10-14)
 * ------------------------------------------------------------------ */

/** What a document is, in a few letters, from its name. */
function kindOf(name: string): string {
  return (name.split('.').pop() ?? '').toUpperCase().slice(0, 4) || 'FILE'
}

/** The last two lines of the thinking summary, without its markdown. */
function lastLines(text: string): string {
  const lines = text
    .replace(/\*\*/g, '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
  const shown = lines.slice(-2).join(' ')
  return shown.length > 220 ? `…${shown.slice(-220)}` : shown
}

/**
 * The model's answer, with the little of Markdown it actually uses: line
 * breaks, **bold**, and links -- [text](address) and bare addresses. The
 * answers carry links since the chat can search the web, and its
 * "**bold**" used to show with its asterisks.
 *
 * A link whose text is just a site's name, as web search writes its citations
 * ("([npv.vn](https://…))"), is drawn as a small chip, as ChatGPT does.
 */
function RichText({ text }: { text: string }) {
  const lines = text.split('\n')
  return (
    <>
      {lines.map((line, index) => (
        <span key={index}>
          {index > 0 ? <br /> : null}
          {inline(line, index)}
        </span>
      ))}
    </>
  )
}

const INLINE = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*\n]+)\*\*|(https?:\/\/[^\s)<>\]]+)/g

function inline(line: string, lineIndex: number): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  for (const found of line.matchAll(INLINE)) {
    const at = found.index ?? 0
    if (at > last) out.push(line.slice(last, at))
    const key = `${lineIndex}-${at}`
    if (found[1] && found[2]) {
      const label = found[1]
      const chip = /^[\w.-]+\.[a-z]{2,}$/i.test(label.trim()) || label.length <= 24
      out.push(
        <a
          key={key}
          href={found[2]}
          target="_blank"
          rel="noreferrer noopener"
          className={chip ? 'project__cite' : 'project__link'}
          title={found[2]}
        >
          {label}
        </a>,
      )
    } else if (found[3]) {
      out.push(<strong key={key}>{found[3]}</strong>)
    } else if (found[4]) {
      out.push(
        <a key={key} href={found[4]} target="_blank" rel="noreferrer noopener" className="project__link">
          {found[4]}
        </a>,
      )
    }
    last = at + found[0].length
  }
  if (last < line.length) out.push(line.slice(last))
  return out
}
